import React, { useState } from 'react';
import styled, { StylesProps, useStyles } from '../../../lib/styles';
import algoliaStyles from './AlgoliaSearch_styles';
import * as logging from '../../../server/logging';
import clsx from 'clsx';
import {
  InstantSearch,
  SearchBox,
  MenuSelect,
  connectMenu,
  connectInfiniteHits,
  connectCurrentRefinements,
  Configure,
} from 'react-instantsearch-dom';
import { searchClient } from '../../../lib/algolia';
import Spacer from '../Spacer';
import Button from '../Button';
import Icon from '../Icon';
import { withQuery } from '../../../lib/stringUtils';
import { pickBy } from 'lodash';

interface Params {
  keyword: string;
  travelType: string;
  destinations: string;
}

export const algoliaSearchIndex = search_index => {
  switch (search_index) {
    case 'Travel':
      return `production_holidays`;
    case 'Marketplace':
      return `production_woocommerce`;
    case 'Media':
      return `production_media`;
    default:
      return `${search_index}`;
  }
};

export const mediaHits = hits => {
  const styles = useStyles(algoliaStyles());
  return (
    <ul className={`${styles.resultContainer} sas-results results-container`}>
      {hits.map(hit => {
        logging.silly(hit, `MediaHits`);
        return (
          <li
            className={`${styles.resultItemContainer} results-media-item results-item-container`}
            key={hit.objectID}
          >
            <div className={`${styles.resultItem} results-item`}>
              <img
                src={
                  hit.image ??
                  `${process.env.BASE_IMGIX_URL}/60-accent-on-purple-landscape.png`
                }
                className={styles.resultImage}
              />
              <div className="author">by {hit.author?.name}</div>
              <div className="result-item-title">{hit.title}</div>
              <div className="result-item-content"></div>
              <div className="result-item-footer">
                <a className="hit-cta" href={hit.slug}>
                  Read more
                </a>
              </div>
            </div>
          </li>
        );
      })}
    </ul>
  );
};

export const travelHits = hits => {
  const styles = useStyles(algoliaStyles());
  logging.silly(styles, 'travelHits');
  return (
    <ul className={`${styles.resultContainer} tas-results results-container`}>
      {hits.map(hit => {
        logging.silly(hit, `TravelHits`);
        return (
          <li
            className={`${styles.resultItemContainer} results-tas-item results-item-container`}
            key={hit.objectID}
          >
            <div className={`${styles.resultItem} results-item`}>
              <img
                src={
                  hit.feature_image ??
                  `${process.env.BASE_IMGIX_URL}/60-accent-on-purple-landscape.png`
                }
                className={styles.resultImage}
              />
              <div className="result-item-title">{hit.title}</div>
              <div className="result-item-content">
                <div>
                  {hit.nights} nights from{' '}
                  <strong>${parseFloat(hit.price_cents) / 100}</strong> pp
                </div>
              </div>
              <div className="result-item-footer">
                <a
                  className="hit-cta"
                  href={`${process.env.BASE_TRAVEL_URL}/holidays/${hit.slug}`}
                >
                  View deal
                </a>
              </div>
            </div>
          </li>
        );
      })}
    </ul>
  );
};

export const marketplaceHits = hits => {
  const styles = useStyles(algoliaStyles());
  return (
    <ul className={`${styles.resultContainer} mktp-results results-container`}>
      {hits.map(hit => {
        logging.silly(hit, `MarketplaceHits`);
        return (
          <li
            className={`${styles.resultItemContainer} results-mktp-item results-item-container`}
            key={hit.objectID}
          >
            <div className={`${styles.resultItem} results-item`}>
              <img
                src={
                  hit.image ??
                  `${process.env.BASE_IMGIX_URL}/60-accent-on-purple-square.png`
                }
                className={styles.resultImage}
              />
              <div className="vendor">by {hit.vendor?.store_name}</div>
              <div className="result-item-title">
                {hit.post_title}
                <div className="price">From {hit.price}</div>
              </div>
              <div className="result-item-content"></div>
              <div className="result-item-footer">
                <a className="hit-cta" href={hit.permalink}>
                  Shop now
                </a>
              </div>
            </div>
          </li>
        );
      })}
    </ul>
  );
};

export interface AlgoliaSearchProps {
  search_index: string;
  search_target?: 'Inline' | 'SearchPage';
}

const AlgoliaSearch = ({
  search_index,
  search_target = 'Inline',
  styles,
}: AlgoliaSearchProps & StylesProps<typeof algoliaStyles>) => {
  const [searchToggle, setSearchToggle] = useState(false);
  const [keyword, setKeyword] = useState('');
  const [travelType, setTravelType] = useState('');
  const [destination, setDestination] = useState('');

  const formatURL = ({ keyword, travelType, destinations }: Params) => {
    return withQuery(
      `/?s=${keyword}`,
      pickBy({
        travelType,
        destinations,
        brand: 'tas',
      })
    );
  };

  const InfiniteHits = ({ hits, hasMore, refineNext }) => (
    <>
      <button
        type="button"
        className={`close`}
        data-dismiss="algolia-search-results"
        aria-label="Close"
        onClick={() => {
          setSearchToggle(false);
        }}
      >
        <span aria-hidden="true">&times;</span>
      </button>
      {travelHits(hits)}
      <button
        className={clsx({ 'd-none': !hasMore })}
        disabled={!hasMore}
        onClick={refineNext}
      >
        Show more
      </button>
    </>
  );
  const CustomInfiniteHits = connectInfiniteHits(InfiniteHits);

  const ClearRefinements = ({ items, refine }) => (
    <button
      onClick={() => {
        refine(items);
        setSearchToggle(false);
      }}
      disabled={!items.length}
    >
      Clear all filters
    </button>
  );
  const CustomClearRefinements = connectCurrentRefinements(ClearRefinements);

  const DestinationMenuSelect = ({ items, currentRefinement, onChange }) => (
    <select value={destination || ''} onChange={event => onChange(event)}>
      <option value="">See all destinations</option>
      {items.map(item => (
        <option
          key={item.label}
          value={item.isRefined ? currentRefinement : item.value}
        >
          {item.label}
        </option>
      ))}
    </select>
  );

  const TravelTypeMenuSelect = ({ items, currentRefinement, onChange }) => (
    <select value={travelType || ''} onChange={event => onChange(event)}>
      <option value="">See all travel types</option>
      {items.map(item => (
        <option
          key={item.label}
          value={item.isRefined ? currentRefinement : item.value}
        >
          {item.label}
        </option>
      ))}
    </select>
  );

  const DestinationSelect = connectMenu(DestinationMenuSelect);
  const TravelTypeSelect = connectMenu(TravelTypeMenuSelect);

  return (
    <div className={`algolia-search-block-container`}>
      <InstantSearch
        searchClient={searchClient}
        indexName={algoliaSearchIndex(search_index)}
      >
        <Configure hitsPerPage={12} />
        <Spacer space={['s', 'm']}>
          <div className="algolia-search-criteria d-flex align-items-stretch">
            <div className="algolia-search-criteria-fields">
              <div className="algolia-search-keyword">
                <label>Search by Keyword</label>
                <SearchBox
                  onChange={e => {
                    setKeyword(e.currentTarget.value);
                    // console.log(`Keyword: ${e.currentTarget.value}`);
                  }}
                />
              </div>
              <div className="algolia-search-destination">
                <label>Select Destination</label>
                <DestinationSelect
                  attribute="destinations"
                  sortBy={'name'}
                  defaultRefinement={''}
                  limit={1000}
                  transformItems={items => {
                    return items
                      .map(item => ({
                        ...item,
                        label:
                          item.label.charAt(0).toUpperCase() +
                          item.label.substr(1).toLowerCase(),
                      }))
                      .sort((a, b) => (a.label < b.label ? -1 : 1));
                  }}
                  translations={{
                    seeAllOption: 'See all destinations',
                  }}
                  onChange={event => {
                    setDestination(event.target.value);
                    // console.log(`Destination: ${event.target.value}`);
                  }}
                />
              </div>
              <div className="algolia-search-type">
                <label>Select Travel Type</label>
                <TravelTypeSelect
                  attribute="types"
                  sortBy={'name'}
                  defaultRefinement={''}
                  limit={1000}
                  transformItems={items => {
                    return items
                      .map(item => ({
                        ...item,
                        label:
                          item.label.charAt(0).toUpperCase() +
                          item.label.substr(1).toLowerCase(),
                      }))
                      .sort((a, b) => (a.label < b.label ? -1 : 1));
                  }}
                  translations={{
                    seeAllOption: 'See all travel types',
                  }}
                  onChange={event => {
                    setTravelType(event.target.value);
                    // console.log(`TravelType: ${event.target.value}`);
                  }}
                />
              </div>
            </div>
            <div className="algolia-search-criteria-controls align-items-stretch">
              <CustomClearRefinements />
              <Button
                className={styles.button}
                onClick={e => {
                  if (search_target == 'SearchPage') {
                    // console.log(`SearchPage: ${keyword} ${travelType} ${destination}`,
                    //   formatURL({ keyword, travelType, destination })
                    // );
                    window.location.href = formatURL({
                      keyword,
                      travelType,
                      destinations: destination,
                    });
                    e.preventDefault();
                    e.stopPropagation();
                  } else {
                    setSearchToggle(true);
                  }
                }}
              >
                <Icon icon={'search'} className={'mr-2'} />
                <span>Search</span>
              </Button>
            </div>
          </div>
        </Spacer>
        <Spacer
          space={['s', 'm']}
          className={clsx(`algolia-search-container`, {
            'd-none': !searchToggle,
          })}
        >
          <CustomInfiniteHits />
        </Spacer>
      </InstantSearch>
    </div>
  );
};

export default styled(algoliaStyles)(AlgoliaSearch);
